// Live launch state. Critical state comes from RPC (`snapshot()` once per new block); history,
// lists and the feed come from the indexer API + SSE.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { encodeAbiParameters, keccak256 } from 'viem';
import type { Address, ContractFunctionReturnType, Hex } from 'viem';
import { launchAbi, tokenAbi } from '@hashcurve/protocol';
import { publicClient } from '../config/chain';
import { api, indexerRetry } from '../lib/api';
import type { FeedItem } from '../lib/api';
import { cleanLabel } from '../lib/sanitize';
import { buildStrip, mergeForges } from '../lib/strip';
import type { ForgeRecord, Strip } from '../lib/strip';
import { useBlockNumber } from './chainClock';
import { useFeedStream } from './useFeedStream';
import type { StreamStatus } from './useFeedStream';

export type Snapshot = ContractFunctionReturnType<typeof launchAbi, 'view', 'snapshot'> & { blockNumber: bigint | undefined };

export const PHASE = { Scheduled: 0, Active: 1, Graduated: 2 } as const;

/** snapshot().challengeBlock of a scheduled launch nobody has ignited yet (type(uint64).max) */
export const NOT_IGNITED = (1n << 64n) - 1n;
export const ZERO_CHALLENGE: Hex = `0x${'0'.repeat(64)}`;
export const isIgnited = (s: Snapshot) => s.challengeBlock !== NOT_IGNITED;

/**
 * The current challenge, available as soon as its opening block is visible (HC-22). An eth_call
 * at that block still reports 0 (it is revealed onchain one block later), but the value is already
 * fixed: keccak256(abi.encode(challengeBase, hash of the opening block)). Mining can start at
 * once; any proof is necessarily submitted in a later block. Returns ZERO_CHALLENGE while the
 * launch is not ignited or the block hash is still loading.
 */
export function useChallenge(launch: Address, snap: Snapshot | undefined): Hex {
  const local = !!snap && snap.phase !== PHASE.Graduated && isIgnited(snap) && snap.challenge === ZERO_CHALLENGE;
  const q = useQuery({
    queryKey: ['challengeLocal', launch, snap?.challengeId.toString(), snap?.challengeBlock.toString()],
    enabled: local,
    staleTime: Infinity,
    queryFn: async () => {
      const block = await publicClient.getBlock({ blockNumber: snap!.challengeBlock });
      return keccak256(encodeAbiParameters([{ type: 'bytes32' }, { type: 'bytes32' }], [snap!.challengeBase, block.hash]));
    },
  });
  if (!snap) return ZERO_CHALLENGE;
  if (!local) return snap.challenge;
  return q.data ?? ZERO_CHALLENGE;
}

export function useSnapshot(launch: Address | undefined) {
  const blockNumber = useBlockNumber();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['snapshot', launch],
    enabled: !!launch,
    staleTime: Infinity,
    retry: 1,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<Snapshot> => {
      const s = await publicClient.readContract({
        address: launch!,
        abi: launchAbi,
        functionName: 'snapshot',
        blockNumber,
      });
      return { ...s, blockNumber };
    },
  });
  // one snapshot() per new block
  useEffect(() => {
    if (launch && blockNumber !== undefined) void qc.invalidateQueries({ queryKey: ['snapshot', launch] });
  }, [qc, launch, blockNumber]);
  return q;
}

export function useTokenInfo(token: Address | undefined) {
  return useQuery({
    queryKey: ['tokenInfo', token],
    enabled: !!token,
    staleTime: Infinity,
    queryFn: async () => {
      const [name, symbol] = await Promise.all([
        publicClient.readContract({ address: token!, abi: tokenAbi, functionName: 'name' }),
        publicClient.readContract({ address: token!, abi: tokenAbi, functionName: 'symbol' }),
      ]);
      return { name: cleanLabel(name), symbol: cleanLabel(symbol) };
    },
  });
}

/** Token balance; `refreshKey` changes (e.g. buys + sells) trigger a refetch. */
export function useTokenBalance(token: Address | undefined, owner: Address | undefined, refreshKey?: unknown) {
  const qc = useQueryClient();
  useEffect(() => {
    if (token && owner) void qc.invalidateQueries({ queryKey: ['balance', token, owner] });
  }, [qc, token, owner, refreshKey]);
  return useQuery({
    queryKey: ['balance', token, owner],
    enabled: !!token && !!owner,
    staleTime: Infinity,
    queryFn: () => publicClient.readContract({ address: token!, abi: tokenAbi, functionName: 'balanceOf', args: [owner!] }),
  });
}

export function useEthBalance(owner: Address | undefined, refreshKey?: unknown) {
  const qc = useQueryClient();
  useEffect(() => {
    if (owner) void qc.invalidateQueries({ queryKey: ['ethBalance', owner] });
  }, [qc, owner, refreshKey]);
  return useQuery({
    queryKey: ['ethBalance', owner],
    enabled: !!owner,
    staleTime: Infinity,
    queryFn: () => publicClient.getBalance({ address: owner! }),
  });
}

export function useLaunchSummary(launch: string | undefined) {
  return useQuery({
    queryKey: ['launch', launch?.toLowerCase()],
    enabled: !!launch,
    queryFn: () => api.launch(launch!),
    refetchInterval: 15_000,
    ...indexerRetry,
  });
}

const order = (i: FeedItem) => i.blockNumber * 10_000 + i.logIndex;
const byNewest = (a: FeedItem, b: FeedItem) => order(b) - order(a);

export type OrderedForge = ForgeRecord & { order: number };

export function forgeRecord(i: FeedItem): OrderedForge | null {
  if (i.type !== 'forge' || !i.sold || !i.amount) return null;
  return { id: i.id, order: order(i), sold: BigInt(i.sold), amount: BigInt(i.amount), difficulty: i.difficultyMetFloat ?? 0 };
}

const MAX_FEED = 300;
const forgesKey = (launch: string | undefined, deep = true) => ['forges', launch?.toLowerCase(), deep];

/**
 * Feed for one launch: newest page from the API plus live SSE items. Calls `onLive` for each
 * live item (after it is merged) so the page can refresh RPC state.
 */
export function useLaunchFeed(launch: string | undefined, onLive?: (item: FeedItem) => void) {
  const qc = useQueryClient();
  const key = ['events', launch?.toLowerCase()];
  const initial = useQuery({
    queryKey: key,
    enabled: !!launch,
    queryFn: () => api.events(launch!, { limit: 100 }),
    ...indexerRetry,
  });
  const [live, setLive] = useState<FeedItem[]>([]);
  const [liveForgeIds] = useState(() => new Set<string>());
  const onLiveRef = useRef(onLive);
  useEffect(() => {
    onLiveRef.current = onLive;
  });

  useEffect(() => {
    setLive([]);
    liveForgeIds.clear();
  }, [launch, liveForgeIds]);

  const status: StreamStatus = useFeedStream({
    launch,
    enabled: !!launch,
    onItem: (item) => {
      if (launch && item.launch.toLowerCase() !== launch.toLowerCase()) return;
      if (item.type === 'forge') {
        liveForgeIds.add(item.id);
        const rec = forgeRecord(item);
        if (rec) qc.setQueryData<OrderedForge[]>(forgesKey(launch), (old) => (old ? mergeForges(old, [rec]) : old));
      }
      setLive((prev) => (prev.some((p) => p.id === item.id) ? prev : [item, ...prev].slice(0, MAX_FEED)));
      onLiveRef.current?.(item);
    },
    onReconnect: () => void qc.invalidateQueries({ queryKey: key }),
    onReorg: () => {
      setLive([]);
      liveForgeIds.clear();
    },
  });

  const items = useMemo(() => {
    const map = new Map<string, FeedItem>();
    for (const i of initial.data?.items ?? []) map.set(i.id, i);
    for (const i of live) map.set(i.id, i);
    return [...map.values()].sort(byNewest).slice(0, MAX_FEED);
  }, [initial.data, live]);

  /** who solved a given challenge (from forge items we have seen) */
  const solverOf = useCallback(
    (challengeId: bigint) => items.find((i) => i.type === 'forge' && BigInt(i.challengeId ?? -1) === challengeId)?.actor,
    [items],
  );

  return { items, status, error: initial.error, isLoading: initial.isLoading, liveForgeIds, solverOf };
}

const PAGE = 200;
const MAX_PAGES = 25;

/**
 * Forge history (newest first) deep enough to colour every filled lot (see lib/strip.ts).
 * Pages through /events until the strip is resolved. Live forges are merged in by useLaunchFeed;
 * when the onchain buy count moves we also merge the newest page to catch anything missed.
 */
export function useForgeHistory(launch: string | undefined, sold: bigint | undefined, buys: bigint | undefined, deep = true) {
  const qc = useQueryClient();
  const key = forgesKey(launch, deep);
  const soldRef = useRef(sold);
  useEffect(() => {
    soldRef.current = sold;
  }, [sold]);
  const q = useQuery({
    queryKey: key,
    enabled: !!launch && sold !== undefined,
    staleTime: Infinity,
    ...indexerRetry,
    queryFn: async () => {
      let forges: OrderedForge[] = [];
      let before: string | undefined;
      for (let page = 0; page < (deep ? MAX_PAGES : 1); page++) {
        const res = await api.events(launch!, { limit: PAGE, before });
        forges = mergeForges(forges, res.items.map(forgeRecord).filter((f): f is OrderedForge => f !== null));
        if (!res.nextCursor || buildStrip(forges, soldRef.current ?? 0n).resolved) break;
        before = res.nextCursor;
      }
      return forges;
    },
  });

  const lastBuys = useRef<bigint | undefined>(undefined);
  const loaded = q.data !== undefined;
  useEffect(() => {
    if (!launch || buys === undefined || !loaded) return;
    if (lastBuys.current !== undefined && buys !== lastBuys.current) {
      void api
        .events(launch, { limit: 50 })
        .then((res) => {
          const fresh = res.items.map(forgeRecord).filter((f): f is OrderedForge => f !== null);
          qc.setQueryData<OrderedForge[]>(forgesKey(launch), (old) => mergeForges(old ?? [], fresh));
        })
        .catch(() => undefined);
    }
    lastBuys.current = buys;
  }, [qc, buys, launch, loaded]);

  const strip: Strip | null = useMemo(
    () => (sold === undefined ? null : buildStrip(q.data ?? [], sold)),
    [q.data, sold],
  );
  return { strip, forges: q.data, isLoading: q.isLoading, error: q.error };
}
